import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { AVGRAnalysis, AVGTAnalysis, TUWTAnalysis } from '../types/health.types';
import { BRAND_COLORS } from '../../../../styles/theme';
import { TrendChart } from './TrendChart';
import { useTranslation } from '../../../../i18n';

const ERROR_COLOR = '#f44336'; // Material-UI error color

// `unknown` is a metric with no validated range (#1207): it shows a neutral
// chip, never the WARNING colour it has not earned.
const statusChipColor = (status: string | undefined) => {
  if (status === 'normal') return 'success';
  if (status === 'warning') return 'warning';
  return 'default';
};

interface AnalysisDisplayProps {
  analysis: AVGRAnalysis | AVGTAnalysis | TUWTAnalysis;
}

export const AnalysisDisplay: React.FC<AnalysisDisplayProps> = ({ analysis }) => {
  switch (analysis.type) {
    case 'AVGR':
      return <AVGRDisplay analysis={analysis} />;
    case 'AVGT':
      return <AVGTDisplay analysis={analysis} />;
    case 'TUWT':
      return <TUWTDisplay analysis={analysis} />;
    default:
      return null;
  }
};

const AVGRDisplay: React.FC<{ analysis: AVGRAnalysis }> = ({ analysis }) => {
  const { t } = useTranslation();

  const formatValue = (value: number | null) => {
    if (value === null) return 'N/A';
    return value.toFixed(2);
  };

  const getStatusLabel = (status: string) => {
    if (status === 'normal') return t('healthPanel.statusNormal');
    if (status === 'warning') return t('healthPanel.statusWarning');
    return t('healthPanel.statusUnknown');
  };

  return (
    <Box sx={{ mb: 1, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {t('healthPanel.latest', { title: analysis.title })}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {formatValue(analysis.value)} {analysis.unit}
        </Typography>
        <Box sx={{ marginLeft: 'auto' }}>
          <Chip
            label={getStatusLabel(analysis.status)}
            color={statusChipColor(analysis.status)}
            size="small"
          />
        </Box>
      </Box>
    </Box>
  );
};

const AVGTDisplay: React.FC<{ analysis: AVGTAnalysis }> = ({ analysis }) => {
  const { t } = useTranslation();

  const formatValue = (value: number | null) => {
    if (value === null) return 'N/A';
    return value.toFixed(2);
  };

  const getStatusLabel = (status: string | undefined) => {
    if (status === 'normal') return t('healthPanel.statusNormal');
    if (status === 'warning') return t('healthPanel.statusWarning');
    return t('healthPanel.statusUnknown');
  };

  return (
    <Box sx={{ mb: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      {/* Latest Value Display */}
      {analysis.latestValue !== null && analysis.latestValue !== undefined && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {t('healthPanel.latest', { title: analysis.title })}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {formatValue(analysis.latestValue)} {analysis.unit}
          </Typography>
          <Box sx={{ marginLeft: 'auto' }}>
            <Chip
              label={getStatusLabel(analysis.status)}
              color={statusChipColor(analysis.status)}
              size="small"
            />
          </Box>
        </Box>
      )}

      {/* The badge reads the whole plotted window, so say which flights it is
          reacting to — otherwise a WARNING beside an in-range latest value
          looks like a mistake (#1269). */}
      {analysis.outOfBandCount ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1, fontSize: '0.75rem' }}
        >
          {t('healthPanel.outOfBandFlights', {
            count: analysis.outOfBandCount,
            total: analysis.windowSize ?? 0,
          })}
        </Typography>
      ) : null}

      {/* Trend Analysis */}
      <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
        {t('healthPanel.trend')}
      </Typography>

      {analysis.forecastValue !== null && (
        <Typography variant="body2" color="text.primary" sx={{ mb: 1, fontSize: '0.85rem' }}>
          {t('healthPanel.forecast')} {formatValue(analysis.forecastValue)} {analysis.unit}
          {analysis.confidence !== undefined && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {t('healthPanel.rSquared', { value: analysis.confidence.toFixed(2) })}
            </Typography>
          )}
          {analysis.forecastDate && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {t('healthPanel.forecastBy', { date: analysis.forecastDate })}
            </Typography>
          )}
        </Typography>
      )}

      {/* Trend Chart */}
      {analysis.chartData && analysis.chartData.length > 0 ? (
        <TrendChart
          unit={analysis.unit}
          dataPoints={analysis.chartData}
          trendValue={analysis.trendValue}
          parameter={analysis.parameter}
        />
      ) : (
        <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('healthPanel.insufficientData')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const TUWTDisplay: React.FC<{ analysis: TUWTAnalysis }> = ({ analysis }) => {
  const { t } = useTranslation();

  const formatValue = (value: number | null) => {
    if (value === null) return 'N/A';
    return value.toFixed(1);
  };

  const getPercentage = (value: number | null, total: number | null) => {
    if (value === null || total === null || total === 0) return 'N/A';
    return ((value / total) * 100).toFixed(1);
  };

  const getTrendLabel = (trend: string) => {
    if (trend === 'improving') return t('healthPanel.trendImproving');
    if (trend === 'worsening') return t('healthPanel.trendWorsening');
    return t('healthPanel.trendStable');
  };

  return (
    <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {analysis.title}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {t('healthPanel.warningTime')}
          </Typography>
          <Typography variant="h6" sx={{ color: BRAND_COLORS.yellow.main }}>
            {formatValue(analysis.warningTime)} {analysis.unit}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('healthPanel.ofTotal', {
              percentage: getPercentage(analysis.warningTime, analysis.totalTime),
            })}
          </Typography>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            {t('healthPanel.errorTime')}
          </Typography>
          <Typography variant="h6" sx={{ color: ERROR_COLOR }}>
            {formatValue(analysis.errorTime)} {analysis.unit}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('healthPanel.ofTotal', {
              percentage: getPercentage(analysis.errorTime, analysis.totalTime),
            })}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('healthPanel.totalMonitoringTime')} {formatValue(analysis.totalTime)} {analysis.unit}
        </Typography>
      </Box>

      <Chip
        label={t('healthPanel.trendLabel', { trend: getTrendLabel(analysis.trend) })}
        color={
          analysis.trend === 'improving'
            ? 'success'
            : analysis.trend === 'worsening'
              ? 'error'
              : 'warning'
        }
        size="small"
      />
    </Box>
  );
};
